import {
  fetchBaseQuery,
  type BaseQueryApi,
  type BaseQueryFn,
  type FetchArgs,
  type FetchBaseQueryError,
  type FetchBaseQueryMeta,
} from '@reduxjs/toolkit/query'
import { getConfig } from '@/shared/config'
import { getLocale } from '@/shared/i18n'
import { endSession, getAccessToken, getSessionGeneration, renewAccessToken } from './tokenStore'

const rawBaseQuery = fetchBaseQuery({
  baseUrl: '',
  credentials: 'include', // refresh-cookie ходит вместе с запросами
  prepareHeaders: (headers) => {
    const token = getAccessToken()
    if (token) headers.set('Authorization', `Bearer ${token}`)
    // Язык интерфейса, а не браузера: его выбирают в шапке. Заголовок CORS-безопасный — без preflight
    headers.set('Accept-Language', getLocale())
    return headers
  },
})

/**
 * Адрес API и предел ожидания ответа — из рантайм-конфига. Предел (`requestTimeoutMs`, A-3)
 * уходит в `fetchBaseQuery` с каждым запросом, а не при его создании: модуль собирается раньше,
 * чем загружен `config.json`. Ручка со своим пределом задаёт `timeout` сама — он главнее.
 * Истёкший предел `fetchBaseQuery` отдаёт как `TIMEOUT_ERROR`, `normalizeError` — как обрыв связи.
 */
function withConfig(args: string | FetchArgs): FetchArgs {
  const { apiBaseUrl, requestTimeoutMs } = getConfig()
  const request = typeof args === 'string' ? { url: args } : args
  return {
    ...request,
    url: `${apiBaseUrl}${request.url}`,
    timeout: request.timeout ?? requestTimeoutMs,
  }
}

async function readJson(response: Response): Promise<unknown> {
  try {
    const body: unknown = await response.json()
    return body
  } catch {
    return undefined
  }
}

/** Ответ refresh: токен; `null` — бэк отказал; `error` — ответа по сути нет (сеть или `5xx`). */
type RefreshResponse = { token: string } | { token: null; error?: FetchBaseQueryError }

async function requestAccessToken(): Promise<RefreshResponse> {
  const { apiBaseUrl, requestTimeoutMs } = getConfig()
  /*
   * Тот же предел ожидания, что у запросов через fetchBaseQuery (A-3): зависший refresh держал бы
   * заставку тихого входа вечно. `AbortSignal.timeout` — Safari 16, вне планки, поэтому таймер свой.
   */
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), requestTimeoutMs)

  let response: Response
  try {
    response = await fetch(`${apiBaseUrl}/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
      signal: controller.signal,
    })
  } catch (error) {
    const failure: FetchBaseQueryError = controller.signal.aborted
      ? { status: 'TIMEOUT_ERROR', error: String(error) }
      : { status: 'FETCH_ERROR', error: String(error) }
    return { token: null, error: failure }
  } finally {
    clearTimeout(timer)
  }

  if (response.status >= 500) {
    return { token: null, error: { status: response.status, data: await readJson(response) } }
  }
  if (!response.ok) return { token: null }

  // Тело — внешние данные: пустой ответ и ответ без строки-токена успехом не считаются (P-AUTH-02#R-05)
  const body = await readJson(response)
  if (typeof body !== 'object' || body === null || !('accessToken' in body)) return { token: null }
  const token = body.accessToken
  return { token: typeof token === 'string' && token !== '' ? token : null }
}

/**
 * Сброс кеша RTK Query. Действие создаёт `baseApi`, а он собран на этом baseQuery: модуль
 * берётся в момент вызова, а не импортом наверху файла — иначе модули замкнулись бы в цикл.
 */
async function resetApiState(dispatch: BaseQueryApi['dispatch']): Promise<void> {
  const { baseApi } = await import('./baseApi')
  dispatch(baseApi.util.resetApiState())
}

/**
 * Итог обновления для запроса, который его ждал:
 * - `renewed` — токен обновлён, запрос повторяется;
 * - `noSession` — сеанса нет или он сменился за время ожидания: запрос отдаёт свой `401`;
 * - `unavailable` — сеть или `5xx`: о сеансе ничего не известно, он не сбрасывается, а запрос
 *   отдаёт эту ошибку вместо `401` — ворота рисуют тупик «Повторить», а не вход (P-AUTH-02#R-03).
 */
type RefreshOutcome =
  { kind: 'renewed' } | { kind: 'noSession' } | { kind: 'unavailable'; error: FetchBaseQueryError }

/**
 * Обновление токена после `401`. Поколение сеанса запоминается на старте: пока ждали ответа,
 * вход мог положить свой токен, а выход — стереть. Тогда ответ refresh относится к прежнему
 * сеансу и ничего не пишет и не стирает.
 *
 * Бэк отказал в обновлении при живом сеансе — сеанс потерян (P-AUTH-02#R-06): токен и кеш
 * сбрасываются, ворота перечитывают сеанс, получают `401` и ведут на вход с плашкой «Сеанс
 * завершён» — о потере им говорит `isSessionEnded()`. Без токена сбрасывать нечего: тихий вход
 * и экран входа просто получают `401`. Иначе ворота, переподписавшись после сброса, запускали
 * бы refresh по кругу.
 */
async function refreshSession(dispatch: BaseQueryApi['dispatch']): Promise<RefreshOutcome> {
  const generation = getSessionGeneration()
  const hadSession = getAccessToken() !== null
  const response = await requestAccessToken()

  if (response.token !== null) {
    return renewAccessToken(response.token, generation)
      ? { kind: 'renewed' }
      : { kind: 'noSession' }
  }
  if (response.error !== undefined) return { kind: 'unavailable', error: response.error }
  if (generation !== getSessionGeneration() || !hadSession) return { kind: 'noSession' }

  endSession()
  await resetApiState(dispatch)
  return { kind: 'noSession' }
}

/**
 * Ручки до входа. Их `401` — ответ формы («неверный логин или пароль»), а не истёкший
 * токен, поэтому обновление токена на них не запускается (решение A-1 комплекта ТЗ).
 */
const PUBLIC_PATHS = [
  '/auth/login',
  '/auth/refresh',
  '/auth/logout',
  '/auth/register',
  '/auth/invitations/',
  '/registration/',
  '/registration-requests',
]

function isPublic(args: string | FetchArgs): boolean {
  const url = typeof args === 'string' ? args : args.url
  return PUBLIC_PATHS.some((path) => url.startsWith(path))
}

/** Ошибка запроса; у `429` — ещё и секунды из заголовка `Retry-After`. */
export type ApiQueryError = FetchBaseQueryError & { retryAfter?: number }

/** `Retry-After` — секунды или HTTP-дата (RFC 9110); не разобрался — `undefined`. */
function parseRetryAfter(header: string | null | undefined): number | undefined {
  if (!header) return undefined

  const seconds = Number(header)
  if (Number.isFinite(seconds)) return Math.max(0, Math.ceil(seconds))

  const date = Date.parse(header)
  return Number.isNaN(date) ? undefined : Math.max(0, Math.ceil((date - Date.now()) / 1000))
}

type RawResult = Awaited<ReturnType<typeof rawBaseQuery>>

/** Форма показывает, сколько ждать: заголовок `429` переносится в саму ошибку. */
function withRetryAfter(
  result: RawResult,
): RawResult | { error: ApiQueryError; meta?: RawResult['meta'] } {
  if (result.error === undefined || result.error.status !== 429) return result

  const retryAfter = parseRetryAfter(result.meta?.response?.headers.get('Retry-After'))
  return retryAfter === undefined ? result : { ...result, error: { ...result.error, retryAfter } }
}

/** Один запрос обновления на все параллельные 401. */
let refreshing: Promise<RefreshOutcome> | null = null

/*
 * Meta пятым параметром — `FetchBaseQueryMeta` сырого `fetchBaseQuery`: `{ request, response }`.
 * Через неё endpoints читают заголовки ответа в `transformResponse` (имя файла из
 * `Content-Disposition` у бинарных выгрузок). Ветка-тупик возвращает ошибку без `meta` — оно
 * опционально в контракте baseQuery.
 */
export const baseQueryWithReauth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  ApiQueryError,
  object,
  FetchBaseQueryMeta
> = async (args, api, extraOptions) => {
  const generation = getSessionGeneration()
  const result = await rawBaseQuery(withConfig(args), api, extraOptions)
  if (result.error?.status !== 401 || isPublic(args)) return withRetryAfter(result)

  // Сеанс сменился, пока шёл запрос: его `401` — про прежний сеанс, обновлять нечего
  if (generation !== getSessionGeneration()) return result

  refreshing ??= refreshSession(api.dispatch).finally(() => {
    refreshing = null
  })
  const outcome = await refreshing

  if (outcome.kind === 'unavailable') return { error: outcome.error }
  if (outcome.kind === 'noSession') return result
  return withRetryAfter(await rawBaseQuery(withConfig(args), api, extraOptions))
}
